import { ScheduledIndicator } from "@circle-react/components/QuickPostV3/Composer/ScheduledIndicator";
import { QUICK_POST_V3_MOBILE_SPACE_SELECT_PORTAL_ID } from "@circle-react/components/QuickPostV3/constants";
import { RequestCloseProvider } from "@circle-react/components/QuickPostV3/contexts/requestCloseContext";
import type { PostFormValues } from "@circle-react/components/QuickPostV3/data/types";
import { useComposerLayout } from "@circle-react/components/QuickPostV3/hooks/useComposerLayout";
import { useKeyboardAvoidingLayout } from "@circle-react/components/QuickPostV3/hooks/useKeyboardAvoidingLayout";
import { usePersistDraft } from "@circle-react/components/QuickPostV3/hooks/usePersistDraft";
import { hasTriggerFormSubmit } from "@circle-react/helpers/hasTriggerFormSubmit";
import { useFormContext } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";

import { Body } from "./Body";
import { Footer } from "./Footer";
import { Header } from "./Header";

interface QuickPostV3ContentProps {
  onRequestClose: () => void;
  shouldPersistDraft?: boolean;
  shouldShowTipTap: boolean;
  variant: "create" | "edit";
}

export const QuickPostV3Content = ({
  onRequestClose,
  shouldPersistDraft = true,
  shouldShowTipTap,
  variant,
}: QuickPostV3ContentProps) => {
  const formContext = useFormContext<PostFormValues>();
  const { isMobile } = useComposerLayout();
  const hasFloatingChrome = isMobile && shouldShowTipTap;
  const containerRef = useKeyboardAvoidingLayout(hasFloatingChrome);

  usePersistDraft({
    enabled: shouldPersistDraft,
    variant,
  });

  useHotkeys(
    "meta+enter, ctrl+enter",
    () => {
      if (hasTriggerFormSubmit(formContext)) {
        formContext.triggerFormSubmit();
      }
    },
    {
      enableOnContentEditable: true,
      enableOnFormTags: ["INPUT", "TEXTAREA"],
    },
    [formContext],
  );

  return (
    <RequestCloseProvider onRequestClose={onRequestClose}>
      <div ref={containerRef} className="flex h-full min-h-0 flex-col">
        {!hasFloatingChrome && (
          <>
            <Header variant={variant} />
            <ScheduledIndicator />
          </>
        )}
        <Body
          hasFloatingChrome={hasFloatingChrome}
          shouldShowTipTap={shouldShowTipTap}
        />
        {!hasFloatingChrome && (
          <>
            <div id={QUICK_POST_V3_MOBILE_SPACE_SELECT_PORTAL_ID} />
            <Footer />
          </>
        )}
      </div>
    </RequestCloseProvider>
  );
};
